<template>
  <Teleport to="body">
    <Transition name="page-window-switcher-fade">
      <div
        v-if="switcherOpen"
        class="page-window-switcher-overlay page-window-switcher-overlay--glass"
        role="dialog"
        aria-modal="true"
        aria-labelledby="page-window-switcher-title"
        @click="closeSwitcher"
      >
        <div
          class="page-window-switcher page-window-switcher--glass"
          @click.stop
        >
          <div class="page-window-switcher__header page-window-switcher__header--glass">
            <h3 id="page-window-switcher-title">
              <span class="page-window-switcher__header-icon" aria-hidden="true">
                <i class="bi bi-window-stack" />
              </span>
              Switch window
            </h3>
            <button
              type="button"
              class="page-window-switcher__header-close"
              title="Close"
              aria-label="Close window switcher"
              @click="closeSwitcher"
            >
              <i class="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          <div class="page-window-switcher__search">
            <i class="bi bi-search" aria-hidden="true" />
            <input
              ref="searchInput"
              v-model.trim="searchQuery"
              type="search"
              placeholder="Search menus"
              aria-label="Search menus"
            >
            <button
              v-if="searchQuery"
              type="button"
              class="page-window-switcher__search-clear"
              aria-label="Clear search"
              @click="clearSearch"
            >
              <i class="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          <ul
            v-if="searchQuery"
            class="page-window-switcher__list page-window-switcher__list--glass"
            aria-label="Menu search results"
          >
            <li
              v-for="(menu, index) in menuSearchResults"
              :key="menu.path"
              class="page-window-switcher__card page-window-switcher__menu-result"
              :style="{ '--pws-card-delay': `${0.04 + index * 0.025}s` }"
              @click="openMenu(menu)"
            >
              <span class="page-window-switcher__card-icon" aria-hidden="true">
                <i :class="menu.icon" />
              </span>
              <div class="page-window-switcher__card-body">
                <span class="page-window-switcher__title">{{ menu.title }}</span>
                <span class="page-window-switcher__menu-group">{{ menu.group }}</span>
              </div>
              <span class="page-window-switcher__open-icon" aria-hidden="true">
                <i class="bi bi-box-arrow-up-right" />
              </span>
            </li>
            <li v-if="menuSearchResults.length === 0" class="page-window-switcher__empty">
              No menus found
            </li>
          </ul>

          <ul
            v-else
            class="page-window-switcher__list page-window-switcher__list--glass"
            role="listbox"
          >
            <li
              v-for="(win, index) in openWindows"
              :key="win.id"
              class="page-window-switcher__card"
              :class="{
                'page-window-switcher__card--hover': index === highlightedIndex,
                'page-window-switcher__card--current': win.id === activeWindowId,
              }"
              :style="{ '--pws-card-delay': `${0.08 + index * 0.045}s` }"
              role="option"
              :aria-selected="win.id === activeWindowId"
              @click="selectWindow(win.id)"
              @mouseenter="highlightedIndex = index"
            >
              <span class="page-window-switcher__card-icon" aria-hidden="true">
                <i :class="getWindowIcon(win)" />
              </span>
              <div class="page-window-switcher__card-body">
                <span class="page-window-switcher__title">{{ win.title }}</span>
                <span
                  v-if="win.id === activeWindowId"
                  class="page-window-switcher__badge"
                >
                  Current window
                </span>
              </div>
              <button
                type="button"
                class="page-window-switcher__close"
                :aria-label="`Close ${win.title}`"
                @click.stop="closeAndStay(win.id)"
              >
                <i class="bi bi-x-lg" aria-hidden="true" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import {
  computed, nextTick, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { getTitleFromRoute, usePageWindows } from '@/composables/usePageWindows';
import { getIconForWindow, getIconFromRoute } from '@/utils/pageWindowIcons';

export default {
  name: 'PageWindowSwitcher',
  setup() {
    const router = useRouter();
    const {
      openWindows,
      activeWindowId,
      switcherOpen,
      switchToWindow,
      closeWindow,
      closeSwitcher,
    } = usePageWindows();

    const highlightedIndex = ref(0);
    const searchQuery = ref('');
    const searchInput = ref(null);

    const searchableMenus = computed(() => {
      const companyId = router.currentRoute.value.params.id;
      if (!companyId) return [];

      const basePath = `/company/${companyId}/dashboard`;
      const seen = new Set();

      return router.getRoutes()
        .filter((route) => route.name && route.path.startsWith('/company/:id/dashboard'))
        .map((route) => {
          const path = route.path.replace(':id', String(companyId));
          if (path.includes(':') || seen.has(path)) return null;
          seen.add(path);

          const title = getTitleFromRoute({ name: route.name, path });
          const relativePath = path.slice(basePath.length).replace(/^\/+/, '');
          const groupSegment = relativePath.split('/')[0] || 'dashboard';
          const group = groupSegment
            .split('-')
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');

          return {
            title,
            path,
            group,
            icon: getIconFromRoute(path),
            searchText: `${title} ${group} ${relativePath} ${String(route.name)}`.toLowerCase(),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.title.localeCompare(b.title));
    });

    const menuSearchResults = computed(() => {
      const terms = searchQuery.value.toLowerCase().split(/\s+/).filter(Boolean);
      if (terms.length === 0) return [];
      return searchableMenus.value
        .filter((menu) => terms.every((term) => menu.searchText.includes(term)))
        .slice(0, 50);
    });

    watch(switcherOpen, (open) => {
      if (open) {
        const activeIdx = openWindows.value.findIndex((w) => w.id === activeWindowId.value);
        highlightedIndex.value = activeIdx >= 0 ? activeIdx : 0;
        searchQuery.value = '';
        nextTick(() => searchInput.value?.focus());
      }
    });

    return {
      openWindows,
      activeWindowId,
      switcherOpen,
      switchToWindow,
      closeWindow,
      closeSwitcher,
      highlightedIndex,
      searchQuery,
      searchInput,
      menuSearchResults,
    };
  },
  methods: {
    getWindowIcon(win) {
      return getIconForWindow(win);
    },
    selectWindow(id) {
      this.switchToWindow(id, this.$router);
      this.closeSwitcher();
    },
    closeAndStay(id) {
      this.closeWindow(id, this.$router);
      if (this.openWindows.length === 0) {
        this.closeSwitcher();
      } else if (this.highlightedIndex >= this.openWindows.length) {
        this.highlightedIndex = Math.max(0, this.openWindows.length - 1);
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    openMenu(menu) {
      this.$router.push(menu.path);
      this.closeSwitcher();
    },
  },
};
</script>

<style src="./styles/PageWindowSwitcher.css"></style>
